import React, { useLayoutEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useColors } from '../theme/ColorModeContext';
import { useChartTheme, withAlpha } from '../components/Admin/Intel/chartTheme';

/** One UTC day as the API reports it (intel._user_activity). */
export interface ActivityDay {
    date: string;
    logins: number;
    visits: number;
    challenge_runs: number;
    theory_answers: number;
    solves: number;
}

export interface ActivityRange {
    start: string;
    end: string;
    days: ActivityDay[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MONO = '"Share Tech Mono", monospace';

// Cells stretch to fill the width between these sizes; whatever does not fit at
// MIN_CELL is dropped from the oldest end, so a phone shows fewer weeks rather
// than a scrollbar, and a wide card shows the whole year rather than empty space.
const GAP = 3;
const MIN_CELL = 10;
const MAX_CELL = 20;
const LABEL_COL = 28;
const DAY_LABELS: [number, string][] = [[0, 'Mon'], [2, 'Wed'], [4, 'Fri']];

/** How much happened on a day, for the calendar's intensity. Solves weigh most;
 *  merely being signed in still shows. */
const dayScore = (day: ActivityDay | undefined) =>
    day ? day.challenge_runs + day.theory_answers + day.solves * 3 + (day.logins || day.visits ? 1 : 0) : 0;

const level = (score: number) => (score === 0 ? 0 : score <= 2 ? 1 : score <= 6 ? 2 : score <= 15 ? 3 : 4);
const LEVEL_ALPHA = [0, 0.25, 0.45, 0.7, 1];

const monthOf = (date: string) =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString([], { month: 'short', timeZone: 'UTC' });

const defaultDescribe = (date: string, day: ActivityDay | undefined) =>
    day
        ? `${date}: ${day.logins} login(s), ${day.visits} visit(s), ${day.challenge_runs} challenge run(s), ${day.theory_answers} answer(s), ${day.solves} solve(s)`
        : `${date}: no activity`;

/** Monday-first weeks from the Monday on or before `start` through `end`. */
const buildWeeks = (range: ActivityRange) => {
    const end = new Date(`${range.end}T00:00:00Z`).getTime();
    const first = new Date(`${range.start}T00:00:00Z`);
    const start = first.getTime() - ((first.getUTCDay() + 6) % 7) * DAY_MS;
    const weeks: string[][] = [];
    for (let time = start; time <= end; time += DAY_MS) {
        if (((time - start) / DAY_MS) % 7 === 0) weeks.push([]);
        weeks[weeks.length - 1].push(new Date(time).toISOString().slice(0, 10));
    }
    return weeks;
};

/** A month name over the column holding its 1st, plus one over the first
 *  column when there is room before the next label. */
const monthLabels = (weeks: string[][]) => {
    const labels: { col: number; text: string }[] = [];
    weeks.forEach((week, col) => {
        const first = week.find((date) => date.endsWith('-01'));
        if (first) labels.push({ col, text: monthOf(first) });
    });
    if (weeks.length && (!labels.length || labels[0].col >= 3)) labels.unshift({ col: 0, text: monthOf(weeks[0][0]) });
    return labels;
};

const useWidth = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) return;
        setWidth(node.clientWidth);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
        observer.observe(node);
        return () => observer.disconnect();
    }, []);
    return [ref, width] as const;
};

interface Props {
    activity: ActivityRange;
    /** Tooltip text for a day; `day` is undefined when nothing happened. */
    describe?: (date: string, day: ActivityDay | undefined) => string;
}

/** Weeks as columns, Monday first, most recent on the right. */
const ActivityCalendar = ({ activity, describe = defaultDescribe }: Props) => {
    const colors = useColors();
    const chart = useChartTheme();
    const [ref, width] = useWidth();
    const byDate = new Map(activity.days.map((day) => [day.date, day]));

    const allWeeks = buildWeeks(activity);
    const fit = Math.floor((width - LABEL_COL) / (MIN_CELL + GAP));
    const weeks = width ? allWeeks.slice(-Math.max(1, Math.min(allWeeks.length, fit))) : [];

    const fill = (alpha: number) => (alpha ? withAlpha(chart.series[0], alpha) : colors.border.subtle);
    const label = { fontFamily: MONO, fontSize: '0.62rem', color: colors.text.muted, lineHeight: 1, whiteSpace: 'nowrap' } as const;

    return (
        <Box ref={ref}>
            {weeks.length > 0 && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: `${LABEL_COL - GAP}px repeat(${weeks.length}, minmax(0, 1fr))`,
                    gap: `${GAP}px`,
                    maxWidth: LABEL_COL + weeks.length * (MAX_CELL + GAP),
                }}>
                    {monthLabels(weeks).map(({ col, text }) => (
                        <Typography key={`${col}-${text}`} sx={{ ...label, gridColumn: col + 2, gridRow: 1, pb: '2px' }}>{text}</Typography>
                    ))}
                    {DAY_LABELS.map(([row, text]) => (
                        <Typography key={text} sx={{ ...label, gridColumn: 1, gridRow: row + 2, alignSelf: 'center' }}>{text}</Typography>
                    ))}
                    {weeks.map((week, col) => week.map((date, row) => {
                        const day = byDate.get(date);
                        return (
                            <Tooltip key={date} title={describe(date, day)} disableInteractive>
                                <Box sx={{
                                    gridColumn: col + 2,
                                    gridRow: row + 2,
                                    aspectRatio: '1 / 1',
                                    borderRadius: '3px',
                                    backgroundColor: fill(LEVEL_ALPHA[level(dayScore(day))]),
                                }} />
                            </Tooltip>
                        );
                    }))}
                </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ ...label, mr: 0.5 }}>Less</Typography>
                    {LEVEL_ALPHA.map((alpha) => (
                        <Box key={alpha} sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: fill(alpha) }} />
                    ))}
                    <Typography sx={{ ...label, ml: 0.5 }}>More</Typography>
                </Box>
                {weeks.length > 0 && (
                    <Typography sx={label}>Last {weeks.length} weeks · UTC days</Typography>
                )}
            </Box>
        </Box>
    );
};

export default ActivityCalendar;
